
import { Component, Vue, Prop, Watch, Emit, Ref } from 'vue-property-decorator'
import BookManager, { EumLoadStatus, EnumScrollDirection } from '@xt/client/utils/book'
import { BookConfig, Chapter, ImgRangeOptions } from '@xt/client/types/book'
import LazyImg from '@xt/www/components/lazyImg/index.vue'
import RealImg from './realImg/index.vue'
import ChapterTOC from './chapterTOC/index.vue'
import Tabs from './tabs/index.vue'
import lodash from 'lodash'
import { Bind, Debounce } from 'lodash-decorators'
import { reportEntryLog } from '@xt/client/utils/log'
import serverTimeUtil from '@xt/client/experimental/server-time'

type MenuItem = {
  key: string
  name: string
}

type Menu = {
  key: string
  name: string
  children: MenuItem[]
}

const defaultBookId = '1'

@Component({
  components: {
    LazyImg,
    Tabs,
    RealImg,
    ChapterTOC
  },
  async asyncData({ query, error, redirect }) {
    let _defaultBookId = defaultBookId

    // 判断file参数不存在默认进第一本书
    if ('id' in query && typeof query.id === 'string') {
      _defaultBookId = query.id
    }

    // 判断该用户是否有查看权限，没有则推到首页
    const res = await BookManager.checkHasAuthView(_defaultBookId)

    if (!res) {
      redirect('/')
    }

    const dataSource = await BookManager.getBook(`book-${_defaultBookId}-pc.json`)

    if (!dataSource) {
      redirect('/404')
    }

    return { dataSource, currentBook: _defaultBookId }
  }
})
export default class BooksPage extends Vue {
  showData: boolean = false
  dataSource: BookConfig
  imgList: {
    url: string
    key: string
  }[] = []
  // tabs数据
  tabsList: string[] = ['2', '1']
  defaultTab: number = 0

  get imageSize() {
    const { width, height } = this.dataSource

    const designWidth = 840

    const designHeigth = lodash.toInteger((designWidth * height) / width)

    return { width: designWidth, height: designHeigth, originWidth: width, originHeight: height }
  }

  currentKeys: string = ''

  get rootSubmenuKeys() {
    return this.dataSource.chapter.map(i => i.key)
  }

  get isFirst() {
    return this.rootSubmenuKeys.findIndex(i => i == this.currentKeys) === 0
  }

  get isLast() {
    return this.rootSubmenuKeys.findIndex(i => i == this.currentKeys) === this.rootSubmenuKeys.length - 1
  }

  @Ref()
  chapterTOC: {
    handlePrev: () => void
    handleNext: () => void
  }

  onUpdateCurrentKeys(key: string) {
    this.currentKeys = key
  }

  onPrev() {
    if (!this.chapterTOC) return

    this.chapterTOC.handlePrev()
  }

  onNext() {
    if (!this.chapterTOC) return

    this.chapterTOC.handleNext()
  }

  handleScrollTo(key: string) {
    // 接收path
    const d = document.querySelector(`#${key}`)

    if (!d) return

    d.scrollIntoView({
      behavior: 'smooth'
    })
  }

  // 初始化
  init() {
    // 获取query参数同步tab状态
    this.defaultTab = this.tabsList.findIndex(tab => tab === this.$route.query.id)
    // 更新key的同时，触发图片列表更新
    this.onUpdateCurrentKeys(this.rootSubmenuKeys[0])
  }

  realImgList: Array<{
    url: string | null
    key: string
  }> = []
  lastActiveKey: number = 0
  currentBook: string = defaultBookId

  get chunkList() {
    return lodash.chunk(this.realImgList, 2)
  }

  // 切换章节更新图片列表数据
  @Watch('currentKeys')
  updateImageList(val) {
    const chapterItem = this.dataSource.chapter.find(el => el.key == val)
    const fileDir = this.dataSource.dir

    let list: {
      url: string
      key: string
    }[] = []

    if (!chapterItem) return

    if (chapterItem.cover) {
      let coverList = Array.isArray(chapterItem.cover) ? chapterItem.cover : [chapterItem.cover]
      coverList.forEach((cPath, cIdx) => {
        list.push({
          url: `${fileDir}${cPath}`,
          key: `chapter-cover-${cIdx}`
        })
      })
    }

    chapterItem.subchapter.forEach((el, idx) => {
      el.path.forEach((p, pIdx) => {
        let url = fileDir + p
        if (pIdx === 0) {
          // 子章节的第一个图需要锚定 追加id参数
          list.push({
            url,
            key: el.key
          })
        } else {
          list.push({
            url,
            key: `${el.key}-${pIdx}`
          })
        }
      })
    })

    this.imgList = list
  }

  @Watch('imgList', { deep: true })
  updateRealImgList(val) {
    // 初始化图片列表数据，避免切换章节时图片列表数据未更新导致会触发预加载限制
    this.realImgList = []
    // 初始化图片 - (加载 - 头部四组)
    this.onLoadRealImgList({
      count: 3
    })
  }

  /**
   * @param data.el 失效图片dom元素
   * 捕捉加载失败(图片链接失效)的图片进行刷新，对于连续多组图片失效的情况，则刷新当前组往下的所有图片
   */
  @Debounce(450, { leading: true, trailing: false })
  @Bind()
  async onRefreshUrlByKey({ el }) {
    const { group, src: imgUrl } = el.dataset as { group: string; src: string } // 组索引
    const activeKey = +group

    console.log('onRefreshUrlByKey', activeKey, imgUrl)

    this.onLoadRealImgList({
      activeKey,
      status: EumLoadStatus.Expired,
      count: 2
    })

    reportEntryLog('debug.book-img.error', {
      now: serverTimeUtil.getServerTime('timestamp'),
      key: imgUrl
    })
  }

  // 预加载图片 - (加载 - 连续两组)
  @Debounce(450)
  @Bind()
  onActivePreLoad(activeKey: number) {
    console.log('onActivePreLoad', activeKey)
    this.onLoadRealImgList({
      count: 2,
      activeKey
    })
  }

  getScrollDirection(currentActiveKey: number) {
    let direction = EnumScrollDirection.Down

    direction = currentActiveKey > this.lastActiveKey ? EnumScrollDirection.Down : EnumScrollDirection.Up

    // 更新上一次索引
    this.lastActiveKey = currentActiveKey

    return direction
  }

  async onLoadRealImgList(options: Omit<ImgRangeOptions, 'startKey'> & { activeKey?: number } = {}) {
    const { activeKey = 0, status = EumLoadStatus.Success } = options

    const dir = this.getScrollDirection(activeKey)
    const totalGroup = this.chunkList.length
    const nextGroupIdx = Math.min(totalGroup - 1, activeKey + 1)
    const prevGroupIdx = Math.max(0, activeKey - 1)

    /**
     * ** 预加载触发限制条件 **
     * 往下连续组没有缓存的情况下才会发请求 - (从上往下连续滚动)
     * 往上连续组没有缓存的情况下才会发请求 - (从下往上连续滚动)
     */
    if (
      status === EumLoadStatus.Success &&
      dir === EnumScrollDirection.Down &&
      this.chunkList.length &&
      this.chunkList[nextGroupIdx].length &&
      this.chunkList[nextGroupIdx][0].url
    )
      return
    if (
      status === EumLoadStatus.Success &&
      dir === EnumScrollDirection.Up &&
      this.chunkList.length &&
      this.chunkList[prevGroupIdx].length &&
      this.chunkList[prevGroupIdx][0].url
    )
      return

    const formatImgList = this.imgList.map(i => i.url)

    const res = await BookManager.getRealUrlByRange(this.currentBook, formatImgList, {
      ...options,
      startKey: activeKey
    })

    // 异常入口访问未授权书籍时，会触发接口异常弹出，则重定向到首页
    if (!res || !Array.isArray(res)) {
      location.replace('/')
      return
    }

    this.realImgList = this.imgList.map((i, idx) => {
      return {
        url: res[idx],
        key: i.key
      }
    })
  }

  created(): void {
    this.init()
  }

  mounted(): void {
    this.$Lazyload.$on('error', this.onRefreshUrlByKey)
  }

  updated(): void {}

  destroyed(): void {
    this.$Lazyload.$off('error', this.onRefreshUrlByKey)

    BookManager.cleanData()
  }
}
